import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {BaseItemObject, InitItemOpts} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE, ItemLoadingProgressType} from '@PosterWhiteboard/items/item/item.types';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHex} from '@Utils/color.util';
import {getDefaultFontSizeForFontFamily, getFancyTextEffectForId, getFancyTextUrl, MorphType} from '@Libraries/fancy-text-library';
import {degToRad} from '@Utils/math.util';
import {openPosterEditorEditFancyTextModal} from '@Modals/poster-editor-fancy-text-modal';
import {loadImageAsync} from '@Utils/image.util';
import {FabricImage} from '@postermywall/fabricjs-2';
import type {DeepPartial} from '@/global';
import type {
  CopyableItemStylesAndProperties,
  FancyTextItemStyles,
} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {pasteStylesForFancyTextItem} from '@PosterWhiteboard/libraries/paste-styles.library';

const SCREEN_SIZE_SCALE = 0.7;
/**
 * Min scale for highres. Equal to the screen size scale
 * as image for that would be cached and loaded instantly
 */
const MIN_HR_SCALE = SCREEN_SIZE_SCALE;
const MAX_HR_SCALE = 2.2;
const VERSION_2 = 2;
const DEFAULT_HEX = '000000';
const MAX_COLORS_AMOUNT = 3;

export interface FancyTextItemObject extends BaseItemObject {
  idFancyText: string;
  text: string;
  fontFamily: string;
  colors: RGBA[];
  morphType: MorphType;
  morphAmount: number;
}

export class FancyTextItem extends Item {
  declare _fabricObject: FabricImage;
  public idFancyText!: string;
  public text!: string;
  public fontFamily!: string;
  public colors: RGBA[] = [];
  public morphType = MorphType.NONE;
  public morphAmount = 0;
  public gitype = ITEM_TYPE.FANCY_TEXT;
  public version = VERSION_2;
  public imageElement!: HTMLImageElement;

  get fabricObject(): FabricImage {
    return this._fabricObject;
  }

  protected fixChanges(applyVersionFixesData: Record<string, any>): void {
    super.fixChanges(applyVersionFixesData);
    this.applyFixForVersion2();
  }

  public toObject(): FancyTextItemObject {
    return {
      ...super.toObject(),
      idFancyText: this.idFancyText,
      text: this.text,
      fontFamily: this.fontFamily,
      colors: this.colors,
      morphType: this.morphType,
      morphAmount: this.morphAmount,
    };
  }

  protected async reInitFabricObject(): Promise<void> {
    this.loading.startLoading({
      langTextKeys: ['pmwjs_updating_percentage'],
      progress: {
        type: ItemLoadingProgressType.FAKE,
        expectedTotalTime: 1,
      },
    });
    try {
      const totalDesiredWidth = this.fabricObject.width * this.fabricObject.scaleX;
      await super.reInitFabricObject();
      const newScale = totalDesiredWidth / this.imageElement.width;
      this.fabricObject.set({
        scaleX: newScale,
        scaleY: newScale,
      });
    } finally {
      this.loading.removeLoading();
    }
  }

  public getCopyableStyles(): CopyableItemStylesAndProperties {
    return {
      ...super.getCopyableStyles(),
      fontFamily: this.fontFamily,
      colors: this.colors,
      idFancyText: this.idFancyText,
      morphType: this.morphType,
      morphAmount: this.morphAmount,
    } as FancyTextItemStyles;
  }

  public async pasteStyles(copiedProperties: CopyableItemStylesAndProperties): Promise<void> {
    await pasteStylesForFancyTextItem(copiedProperties, this);
  }

  public async formatItemObjectWithDefaultValues(obj: DeepPartial<FancyTextItemObject>): Promise<DeepPartial<FancyTextItemObject>> {
    if (!obj.idFancyText) {
      throw new Error('no idFancyText for new fancy text item');
    }

    const fancyTextEffect = await getFancyTextEffectForId(obj.idFancyText);
    return {
      colors: fancyTextEffect.defaultColors,
      fontFamily: fancyTextEffect.defaultFamilyName,
      ...obj,
    };
  }

  protected itemObjectHasDestructiveChanges(oldItemObject: FancyTextItemObject): boolean {
    return (
      oldItemObject.idFancyText !== this.idFancyText ||
      oldItemObject.text !== this.text ||
      oldItemObject.fontFamily !== this.fontFamily ||
      oldItemObject.colors !== this.colors ||
      oldItemObject.morphType !== this.morphType ||
      oldItemObject.morphAmount !== this.morphAmount
    );
  }

  protected async getFabricObjectForItem(opts: InitItemOpts = {}): Promise<FabricImage> {
    this.imageElement = await loadImageAsync(this.getImageUrl(this.getScale()));
    const fabricOpts: Record<string, unknown> = {};

    if (opts?.width !== undefined && opts?.scaleX !== undefined) {
      const totalDesiredWidth = opts.width * opts.scaleX;
      const newScale = totalDesiredWidth / this.imageElement.width;
      fabricOpts.scaleX = newScale;
      fabricOpts.scaleY = newScale;
    }

    return new FabricImage(this.imageElement, fabricOpts);
  }

  private getScale(): number {
    if (this.page.poster.isHighRes) {
      return MAX_HR_SCALE;
    }

    return SCREEN_SIZE_SCALE;
  }

  private applyFixForVersion2(): void {
    if (this.version < VERSION_2) {
      const changeInHeightForVersion2 = 8;
      const deltaY = changeInHeightForVersion2 * this.fabricObject.scaleY;
      const cosOfAngle = Math.cos(degToRad(this.fabricObject.angle));
      const sinOfAngle = Math.sin(degToRad(this.fabricObject.angle));

      this.fabricObject.set({
        left: this.fabricObject.left + sinOfAngle * deltaY,
        top: this.fabricObject.top - cosOfAngle * deltaY,
      });
      this.version = VERSION_2;
    }
  }

  private getImageUrl(scale: number): string {
    return getFancyTextUrl(
      this.idFancyText,
      this.fontFamily,
      getDefaultFontSizeForFontFamily(this.fontFamily),
      scale,
      this.text,
      this.getColorHexes(),
      this.morphType,
      this.morphAmount,
      this.version
    );
  }

  private getColorHexes(): string[] {
    const colorHexes = [];
    for (const color of this.colors) {
      colorHexes.push(rgbaToHex(color, false));
    }

    const lengthOfColorHexes = colorHexes.length;

    for (let index = 0; index < MAX_COLORS_AMOUNT - lengthOfColorHexes; index += 1) {
      colorHexes.push(DEFAULT_HEX);
    }

    return colorHexes;
  }

  protected onItemDoubleTapped(): void {
    openPosterEditorEditFancyTextModal();
  }

  public updateFancyTextEffect(id: string, colors: RGBA[]): Promise<void> {
    return this.updateFromObject({
      idFancyText: id,
      colors,
    });
  }

  public updateFancyTextFont(fontFamily: string): Promise<void> {
    return this.updateFromObject({
      fontFamily,
    });
  }

  public updateTextContent(value: string, undoable = true): void {
    void this.updateFromObject(
      {
        text: value,
      },
      {
        undoable,
      }
    );
  }

  public isMorphAmountApplicable(): boolean {
    return this.morphType === MorphType.CURVE_UP || this.morphType === MorphType.CURVE_DOWN || this.morphType === MorphType.WAVE;
  }
}
